import { ChevronDown, ChevronRight, RefreshCw } from "lucide-react";
import { PlatformBadge } from "./PlatformBadge";
import type { UnifiedCampaign, UnifiedAdGroup, UnifiedAd, UnifiedRow } from "./types";

export type ColDef = {
  key: string;
  label: string;
  align?: "left" | "right" | "center";
  sortable?: boolean;
  width?: string;
  tiktokOnly?: boolean;
  metaOnly?: boolean;
};

function StatusBadge({ status }: { status: string }) {
  const s = status.toUpperCase();
  const color = s === "ACTIVE" ? "#31A24C" : s === "PAUSED" ? "#F5A623" : "#606770";
  const background = s === "ACTIVE" ? "#E6F4EA" : s === "PAUSED" ? "#FFF8E1" : "#F0F2F5";
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[11px] font-medium" style={{ color, background }} data-testid={`status-${s.toLowerCase()}`}>
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: color }} />
      {s.charAt(0) + s.slice(1).toLowerCase()}
    </span>
  );
}

function formatCurrency(v: number): string {
  return "$" + v.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}
function formatNumber(v: number): string {
  return v.toLocaleString();
}
function formatPct(v: number): string {
  return v.toFixed(2) + "%";
}

export function getMetricValue(item: UnifiedRow, key: string): string {
  const m = item.metrics;
  switch (key) {
    case "spend": return formatCurrency(m.spend);
    case "impressions": return formatNumber(m.impressions);
    case "clicks": return formatNumber(m.clicks);
    case "reach": return formatNumber(m.reach);
    case "ctr": return formatPct(m.ctr);
    case "cpc": return formatCurrency(m.cpc);
    case "cpm": return formatCurrency(m.cpm);
    case "conversions": return formatNumber(m.conversions);
    case "roas": return m.roas + "x";
    case "purchaseValue": return formatCurrency(m.purchaseValue);
    case "costPerResult": return m.costPerResult > 0 ? formatCurrency(m.costPerResult) : "—";
    case "videoViews": return formatNumber(m.videoViews || 0);
    case "completionRate": {
      const views = m.videoViews || 0;
      const completions = m.videoCompletions || 0;
      return views > 0 ? formatPct((completions / views) * 100) : "—";
    }
    case "sixSecondViews": return formatNumber(m.sixSecondViews || 0);
    case "likes": return formatNumber(m.likes || 0);
    case "comments": return formatNumber(m.comments || 0);
    case "shares": return formatNumber(m.shares || 0);
    case "saves": return formatNumber(m.saves || 0);
    case "profileVisits": return formatNumber(m.profileVisits || 0);
    case "qualityRanking": return "—";
    case "engagementRanking": return "—";
    case "outboundClicks": return formatNumber(m.clicks);
    case "budget": {
      if ("dailyBudget" in item && item.dailyBudget) return formatCurrency(item.dailyBudget) + "/d";
      if ("lifetimeBudget" in item && item.lifetimeBudget) return formatCurrency(item.lifetimeBudget);
      return "—";
    }
    default: return "—";
  }
}

export function getSortableValue(item: UnifiedRow, key: string): number {
  const m = item.metrics;
  switch (key) {
    case "spend": return m.spend;
    case "impressions": return m.impressions;
    case "clicks": return m.clicks;
    case "reach": return m.reach;
    case "ctr": return m.ctr;
    case "cpc": return m.cpc;
    case "cpm": return m.cpm;
    case "conversions": return m.conversions;
    case "roas": return m.roas;
    case "purchaseValue": return m.purchaseValue;
    case "costPerResult": return m.costPerResult;
    case "videoViews": return m.videoViews || 0;
    case "completionRate": {
      const views = m.videoViews || 0;
      return views > 0 ? ((m.videoCompletions || 0) / views) * 100 : 0;
    }
    case "sixSecondViews": return m.sixSecondViews || 0;
    case "likes": return m.likes || 0;
    case "shares": return m.shares || 0;
    case "saves": return m.saves || 0;
    case "outboundClicks": return m.clicks;
    default: return 0;
  }
}

export interface CampaignTableProps {
  groupedCampaigns: { label: string; items: UnifiedCampaign[] }[];
  columns: ColDef[];
  sortKey: string;
  sortDir: "asc" | "desc";
  onSort: (key: string) => void;
  expandedCampaigns: Set<number>;
  expandedAdGroups: Set<number>;
  adGroupsMap: Record<number, UnifiedAdGroup[]>;
  adsMap: Record<number, UnifiedAd[]>;
  onToggleCampaign: (id: number) => void;
  onToggleAdGroup: (id: number) => void;
  onDetail: (v: { item: UnifiedRow; type: "campaign" | "adgroup" | "ad" }) => void;
  showGroupHeaders: boolean;
  isLoading: boolean;
  totalCount: number;
  totalSpend: number;
  totalConversions: number;
}

export function CampaignTable({
  groupedCampaigns, columns, sortKey, sortDir, onSort,
  expandedCampaigns, expandedAdGroups, adGroupsMap, adsMap,
  onToggleCampaign, onToggleAdGroup, onDetail,
  showGroupHeaders, isLoading, totalCount, totalSpend, totalConversions,
}: CampaignTableProps) {
  return (
    <div className="overflow-x-auto">
      <table className="w-full" style={{ fontSize: "13px", color: "#1C2B33" }} data-testid="unified-campaign-table">
        <thead>
          <tr style={{ background: "#F5F6F7", borderBottom: "1px solid #DAE0E5" }}>
            <th className="w-10 px-2 py-2" />
            <th className="px-3 py-2 text-left text-xs font-semibold uppercase min-w-[260px]" style={{ color: "#606770" }}>
              Name
            </th>
            {columns.map(col => (
              <th key={col.key}
                className="px-3 py-2 text-xs font-semibold uppercase whitespace-nowrap"
                style={{ color: "#606770", textAlign: col.align || "left", cursor: col.sortable ? "pointer" : "default", minWidth: col.width }}
                onClick={() => col.sortable && onSort(col.key)}>
                <span className="inline-flex items-center gap-1">
                  {col.label}
                  {col.sortable && sortKey === col.key && <span className="text-[10px]">{sortDir === "asc" ? "▲" : "▼"}</span>}
                </span>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {isLoading ? (
            <tr><td colSpan={columns.length + 2} className="text-center py-12">
              <div className="flex items-center justify-center gap-2">
                <RefreshCw className="w-4 h-4 animate-spin" style={{ color: "#606770" }} />
                <span className="text-sm" style={{ color: "#606770" }}>Loading campaigns...</span>
              </div>
            </td></tr>
          ) : totalCount === 0 ? (
            <tr><td colSpan={columns.length + 2} className="text-center py-12 text-sm" style={{ color: "#606770" }}>No campaigns found</td></tr>
          ) : (
            groupedCampaigns.map((group, gi) => (
              <GroupRows key={gi} group={group} columns={columns}
                expandedCampaigns={expandedCampaigns} expandedAdGroups={expandedAdGroups}
                adGroupsMap={adGroupsMap} adsMap={adsMap}
                onToggleCampaign={onToggleCampaign} onToggleAdGroup={onToggleAdGroup}
                onDetail={onDetail} showGroupHeader={showGroupHeaders} />
            ))
          )}
          {totalCount > 0 && (
            <tr style={{ borderTop: "2px solid #DAE0E5", background: "#F5F6F7" }}>
              <td className="px-2 py-2" />
              <td className="px-3 py-2"><span className="text-xs font-semibold" style={{ color: "#606770" }}>Totals ({totalCount})</span></td>
              {columns.map(col => (
                <td key={col.key} className="px-3 py-2 whitespace-nowrap text-xs font-semibold" style={{ textAlign: col.align || "left", color: "#1C2B33" }}>
                  {col.key === "spend" ? formatCurrency(totalSpend) : col.key === "conversions" ? formatNumber(totalConversions) : ""}
                </td>
              ))}
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}

interface GroupRowsProps {
  group: { label: string; items: UnifiedCampaign[] };
  columns: ColDef[];
  expandedCampaigns: Set<number>;
  expandedAdGroups: Set<number>;
  adGroupsMap: Record<number, UnifiedAdGroup[]>;
  adsMap: Record<number, UnifiedAd[]>;
  onToggleCampaign: (id: number) => void;
  onToggleAdGroup: (id: number) => void;
  onDetail: (v: { item: UnifiedRow; type: "campaign" | "adgroup" | "ad" }) => void;
  showGroupHeader: boolean;
}

function GroupRows({ group, columns, expandedCampaigns, expandedAdGroups, adGroupsMap, adsMap, onToggleCampaign, onToggleAdGroup, onDetail, showGroupHeader }: GroupRowsProps) {
  return (
    <>
      {showGroupHeader && group.label && (
        <tr style={{ background: "#EBEDF0", borderBottom: "1px solid #DAE0E5" }}>
          <td colSpan={columns.length + 2} className="px-4 py-2">
            <span className="text-xs font-bold uppercase" style={{ color: "#606770" }}>{group.label}</span>
            <span className="text-xs ml-2" style={{ color: "#8D949E" }}>({group.items.length})</span>
          </td>
        </tr>
      )}
      {group.items.map(campaign => {
        const isExpanded = expandedCampaigns.has(campaign.id);
        const adGroups = adGroupsMap[campaign.id] || [];
        return (
          <CampaignTreeRow key={campaign.id} campaign={campaign} isExpanded={isExpanded} adGroups={adGroups}
            expandedAdGroups={expandedAdGroups} adsMap={adsMap} columns={columns}
            onToggle={() => onToggleCampaign(campaign.id)} onToggleAdGroup={onToggleAdGroup} onDetail={onDetail} />
        );
      })}
    </>
  );
}

function CampaignTreeRow({ campaign, isExpanded, adGroups, expandedAdGroups, adsMap, columns, onToggle, onToggleAdGroup, onDetail }: {
  campaign: UnifiedCampaign; isExpanded: boolean; adGroups: UnifiedAdGroup[]; expandedAdGroups: Set<number>;
  adsMap: Record<number, UnifiedAd[]>; columns: ColDef[]; onToggle: () => void;
  onToggleAdGroup: (id: number) => void; onDetail: (v: { item: UnifiedRow; type: "campaign" | "adgroup" | "ad" }) => void;
}) {
  return (
    <>
      <tr className="group hover:bg-gray-50 cursor-pointer" style={{ borderBottom: "1px solid #E4E6EB" }}
        onClick={() => onDetail({ item: campaign, type: "campaign" })} data-testid={`row-campaign-${campaign.id}`}>
        <td className="px-2 py-2 text-center" onClick={e => { e.stopPropagation(); onToggle(); }}>
          {isExpanded ? <ChevronDown className="w-4 h-4 text-gray-500" /> : <ChevronRight className="w-4 h-4 text-gray-400" />}
        </td>
        <td className="px-3 py-2 min-w-[260px]">
          <div className="flex items-center gap-2">
            <PlatformBadge slug={campaign.platformSlug} />
            <div className="min-w-0">
              <span className="text-[13px] font-medium truncate block max-w-[240px]" style={{ color: "#1877F2" }} data-testid={`link-campaign-${campaign.id}`}>{campaign.name}</span>
              <p className="text-[11px] truncate" style={{ color: "#606770" }}>{campaign.accountName}</p>
            </div>
          </div>
        </td>
        {columns.map(col => (
          <td key={col.key} className="px-3 py-2 whitespace-nowrap" style={{ textAlign: col.align || "left", fontSize: "13px" }}>
            {col.key === "platform" ? <PlatformBadge slug={campaign.platformSlug} size="md" />
              : col.key === "status" ? <StatusBadge status={campaign.status} />
              : <span style={{ fontWeight: col.key === "spend" ? 600 : 400 }}>{getMetricValue(campaign, col.key)}</span>}
          </td>
        ))}
      </tr>
      {isExpanded && adGroups.map(ag => {
        const agExpanded = expandedAdGroups.has(ag.id);
        const ads = adsMap[ag.id] || [];
        return (
          <AdGroupTreeRow key={ag.id} adGroup={ag} isExpanded={agExpanded} ads={ads} columns={columns}
            onToggle={() => onToggleAdGroup(ag.id)} onDetail={onDetail} platformSlug={campaign.platformSlug} />
        );
      })}
      {isExpanded && adGroups.length === 0 && (
        <tr style={{ borderBottom: "1px solid #E4E6EB" }}>
          <td colSpan={columns.length + 2} className="pl-12 py-2 text-xs" style={{ color: "#606770" }}>
            No ad sets found
          </td>
        </tr>
      )}
    </>
  );
}

function AdGroupTreeRow({ adGroup, isExpanded, ads, columns, onToggle, onDetail, platformSlug }: {
  adGroup: UnifiedAdGroup; isExpanded: boolean; ads: UnifiedAd[]; columns: ColDef[];
  onToggle: () => void; onDetail: (v: { item: UnifiedRow; type: "campaign" | "adgroup" | "ad" }) => void; platformSlug: string;
}) {
  return (
    <>
      <tr className="group hover:bg-blue-50/30 cursor-pointer" style={{ borderBottom: "1px solid #E4E6EB", background: "#FAFBFC" }}
        onClick={() => onDetail({ item: adGroup, type: "adgroup" })} data-testid={`row-adgroup-${adGroup.id}`}>
        <td className="px-2 py-2 text-center" onClick={e => { e.stopPropagation(); onToggle(); }}>
          <div className="ml-4">{isExpanded ? <ChevronDown className="w-3.5 h-3.5 text-gray-500" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}</div>
        </td>
        <td className="px-3 py-2 min-w-[260px]">
          <div className="flex items-center gap-2 pl-4">
            <div className="w-px h-4 bg-gray-300" />
            <span className="text-[12px] font-medium truncate block max-w-[220px]" style={{ color: "#4B5563" }} data-testid={`link-adgroup-${adGroup.id}`}>
              {adGroup.name}
            </span>
          </div>
        </td>
        {columns.map(col => (
          <td key={col.key} className="px-3 py-1.5 whitespace-nowrap" style={{ textAlign: col.align || "left", fontSize: "12px", color: "#4B5563" }}>
            {col.key === "platform" ? <PlatformBadge slug={platformSlug} size="sm" />
              : col.key === "status" ? <StatusBadge status={adGroup.status} />
              : getMetricValue(adGroup, col.key)}
          </td>
        ))}
      </tr>
      {isExpanded && ads.map(ad => (
        <tr key={ad.id} className="group hover:bg-indigo-50/20 cursor-pointer" style={{ borderBottom: "1px solid #F0F2F5", background: "#F7F8FA" }}
          onClick={() => onDetail({ item: ad, type: "ad" })} data-testid={`row-ad-${ad.id}`}>
          <td className="px-2 py-1.5" />
          <td className="px-3 py-1.5 min-w-[260px]">
            <div className="flex items-center gap-2 pl-10">
              <div className="w-px h-3 bg-gray-200" />
              {ad.thumbnailUrl || ad.imageUrl ? (
                <img src={ad.thumbnailUrl || ad.imageUrl || ""} alt="" className="w-6 h-6 rounded object-cover flex-shrink-0" />
              ) : (
                <div className="w-6 h-6 rounded bg-gray-200 flex items-center justify-center flex-shrink-0">
                  <span className="text-[8px] text-gray-400">{ad.creativeType === "video" ? "▶" : "🖼"}</span>
                </div>
              )}
              <span className="text-[11px] truncate max-w-[180px]" style={{ color: "#606770" }} data-testid={`link-ad-${ad.id}`}>{ad.name}</span>
            </div>
          </td>
          {columns.map(col => (
            <td key={col.key} className="px-3 py-1.5 whitespace-nowrap" style={{ textAlign: col.align || "left", fontSize: "11px", color: "#606770" }}>
              {col.key === "platform" ? <PlatformBadge slug={ad.platformSlug} size="sm" />
                : col.key === "status" ? <StatusBadge status={ad.status} />
                : getMetricValue(ad, col.key)}
            </td>
          ))}
        </tr>
      ))}
      {isExpanded && ads.length === 0 && (
        <tr style={{ borderBottom: "1px solid #F0F2F5" }}>
          <td colSpan={columns.length + 2} className="pl-16 py-1.5 text-[11px]" style={{ color: "#606770" }}>
            No ads found
          </td>
        </tr>
      )}
    </>
  );
}
